/**
 * Text formatting utilities for cleaning and formatting markdown content
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize an HTML fragment that's about to land in
 * `dangerouslySetInnerHTML`. The text formatter already runs every
 * user/AI-supplied piece through escapeHtml() before splicing it into
 * the template, but DOMPurify is the second layer of defense: if any
 * layer ever forgets to escape (or the AI provider ever returns markup with
 * raw markup we trusted), the sanitizer strips scripts/iframes/event
 * handlers before they reach the DOM. Tag list reflects what the
 * formatter actually emits — anything else is dropped.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'span', 'div',
  'a',
  'blockquote', 'code', 'pre',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'hr', 'sup', 'sub',
];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'id', 'colspan', 'rowspan'];

export function sanitizeHtml(input: string): string {
  if (!input) return '';
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'link', 'meta'],
    FORBID_ATTR: ['style', 'onclick', 'onerror', 'onload', 'onmouseover', 'srcdoc', 'formaction'],
    ALLOW_DATA_ATTR: false,
  }) as unknown as string;
}

// One-time hook so any rendered <a target="_blank"> gets noopener/noreferrer
// automatically — protects against window.opener tab-napping.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if ((node as HTMLElement).tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Cleans markdown syntax and formats text for better readability
 * Removes markdown markers like **, ##, *, etc. and formats as clean HTML
 */
export function cleanMarkdown(text: string): string {
  if (!text) return '';

  let cleaned = text;

  // Remove markdown headers (##, ###, ####, etc.) - but preserve the text
  cleaned = cleaned.replace(/^#{1,6}\s+(.+)$/gm, '$1');

  // Remove bold markers (**text** or __text__) - preserve the text
  // Do this first so we don't confuse bold with italic
  cleaned = cleaned.replace(/\*\*([^*]+?)\*\*/g, '$1');
  cleaned = cleaned.replace(/__([^_]+?)__/g, '$1');

  // Remove italic markers (*text* or _text_) - but be careful with bullet points
  // After bold is removed, remove remaining single * and _ that are not at line start
  // Process line by line to avoid removing bullet point markers
  cleaned = cleaned.split('\n').map(line => {
    // If line starts with * or - followed by space, it's a bullet point - don't touch it
    if (/^[\s]*[-*•]\s/.test(line)) {
      return line;
    }
    // Otherwise, remove italic markers
    line = line.replace(/\*([^*\n]+?)\*/g, '$1');
    line = line.replace(/_([^_\n]+?)_/g, '$1');
    return line;
  }).join('\n');

  // Remove code blocks (```code```)
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '');

  // Remove inline code (`code`)
  cleaned = cleaned.replace(/`([^`]+?)`/g, '$1');

  // Remove markdown links [text](url) -> text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');

  // Remove markdown images ![alt](url)
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^\)]+\)/g, '');

  // Remove horizontal rules (---, ***, ___) on their own line
  cleaned = cleaned.replace(/^[-*_]{3,}$/gm, '');

  // Remove strikethrough (~~text~~)
  cleaned = cleaned.replace(/~~(.*?)~~/g, '$1');

  // Clean up multiple spaces (but preserve single spaces)
  cleaned = cleaned.replace(/[ \t]{2,}/g, ' ');

  // Clean up multiple newlines (more than 2 consecutive)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');

  return cleaned.trim();
}

/**
 * Formats text content as HTML with proper paragraph breaks and styling
 */
export function formatTextAsHTML(text: string): string {
  if (!text) return '';

  // First clean markdown
  let formatted = cleanMarkdown(text);

  // Split into paragraphs (double newlines)
  const paragraphs = formatted.split(/\n\n+/).filter(p => p.trim().length > 0);

  // Format each paragraph
  const formattedParagraphs = paragraphs.map(paragraph => {
    let para = paragraph.trim();

    // Handle bullet points (lines starting with -, *, or •)
    if (/^[-*•]\s/.test(para) || /^\d+\.\s/.test(para)) {
      // It's a list, format as list items
      const lines = para.split('\n');
      const listItems = lines
        .filter(line => line.trim().length > 0)
        .map(line => {
          // Remove bullet markers
          line = line.replace(/^[-*•]\s+/, '');
          line = line.replace(/^\d+\.\s+/, '');
          return `<li class="ml-4 mb-2">${escapeHtml(line.trim())}</li>`;
        })
        .join('');
      return `<ul class="list-disc space-y-1 my-4">${listItems}</ul>`;
    }

    // Regular paragraph
    // Replace single newlines with <br> for line breaks within paragraphs
    para = para.replace(/\n/g, '<br>');
    
    // Escape HTML but preserve our <br> tags
    para = escapeHtml(para);
    para = para.replace(/&lt;br&gt;/g, '<br>');

    return `<p class="mb-4 leading-relaxed">${para}</p>`;
  });

  return formattedParagraphs.join('');
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * Format text with improved readability styling
 * This is a simpler version that just cleans markdown and adds basic formatting
 */
export function formatContentForDisplay(text: string): string {
  if (!text) return '';

  // Clean markdown first
  let content = cleanMarkdown(text);

  // Split by double newlines to get paragraphs/sections
  const sections = content.split(/\n\n+/).filter(s => s.trim().length > 0);

  const formattedSections = sections.map(section => {
    const trimmed = section.trim();
    if (!trimmed) return '';

    // Split into lines to check for lists and headings
    const lines = trimmed.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    
    // Check if most lines are list items
    const listLineCount = lines.filter(line => 
      /^[-*•]\s/.test(line) || /^\d+\.\s/.test(line)
    ).length;
    
    const isList = listLineCount > 0 && listLineCount >= lines.length * 0.5;

    if (isList) {
      // Format as list (no bullet points for puanlama content)
      const listItems = lines
        .map(line => {
          // Remove list markers
          let item = line.replace(/^[-*•]\s+/, '').replace(/^\d+\.\s+/, '').trim();
          // Clean any remaining markdown
          item = item.replace(/\*\*/g, '').replace(/__/g, '');
          item = escapeHtml(item);
          return `<li class="mb-2 ml-0" style="list-style: none;">${item}</li>`;
        })
        .join('');
      return `<ul class="space-y-2 my-4 ml-0" style="list-style: none; padding-left: 0;">${listItems}</ul>`;
    }

    // Regular paragraph - detect and format headings
    let formattedLines: string[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextLine = i < lines.length - 1 ? lines[i + 1] : '';
      
      // Detect headings: lines that end with colon, are short, or are followed by empty line
      // First, clean the line to check for heading patterns
      let cleanedLineForCheck = line.trim();
      cleanedLineForCheck = cleanedLineForCheck.replace(/^[\s]*[-*•]\s+/, '');
      cleanedLineForCheck = cleanedLineForCheck.replace(/^\d+\.\s+/, '');
      
      const isHeading = (
        cleanedLineForCheck.endsWith(':') && 
        cleanedLineForCheck.length < 100
      ) || (
        cleanedLineForCheck.length < 80 && 
        nextLine && 
        nextLine.length > 50 &&
        !cleanedLineForCheck.includes('•') &&
        !cleanedLineForCheck.includes(' - ')
      );
      
      if (isHeading) {
        // Remove bullet points from headings (•, -, *, and various Unicode bullet characters)
        let headingText = line.trim();
        // Remove leading bullet points (more comprehensive pattern)
        headingText = headingText.replace(/^[\s]*[-*•·◦‣⁃]\s+/, ''); // Remove various bullet characters
        headingText = headingText.replace(/^\d+[.)]\s+/, ''); // Remove leading numbers with dot or parenthesis
        headingText = headingText.replace(/^[\s]*[a-zA-Z][.)]\s+/, ''); // Remove leading letters with dot or parenthesis (a., b., etc.)
        // Remove markdown bold markers (**) from anywhere in the heading
        headingText = headingText.replace(/\*\*/g, ''); // Remove all ** markers
        headingText = headingText.trim();
        
        // Extract score from heading (format: "Heading: [8.5]/10" or "Heading: 8.5/10")
        const scoreMatch = headingText.match(/:?\s*\[?([0-9,.]+)\]?\s*\/\s*10/);
        let scoreDisplay = '';
        if (scoreMatch) {
          const score = scoreMatch[1].replace(',', '.');
          // Remove the score part and trailing colon if present
          headingText = headingText.replace(/:?\s*\[?[0-9,.]+\]?\s*\/\s*10/g, '').replace(/:$/, '').trim();
          scoreDisplay = `<span class="inline-block ml-2 px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-md text-sm font-semibold text-slate-900 dark:text-slate-100">${escapeHtml(score)}</span>`;
        }
        
        // Format as bold heading with spacing and score
        const escapedLine = escapeHtml(headingText);
        formattedLines.push(`<p class="font-bold text-slate-900 dark:text-white text-lg mb-2 mt-6 leading-tight">${escapedLine}${scoreDisplay}</p>`);
        // Add extra spacing after heading
        if (nextLine) {
          formattedLines.push(`<div class="mb-1"></div>`);
        }
      } else {
        // Regular paragraph line
        let para = line;
        // Clean any remaining markdown artifacts
        para = para.replace(/\*\*/g, '').replace(/__/g, '');
        para = escapeHtml(para);
        
        // Check if this is a continuation of previous paragraph or new paragraph
        if (i === 0 || lines[i - 1].endsWith(':') || lines[i - 1].length < 80) {
          // Start new paragraph
          formattedLines.push(`<p class="mb-4 leading-relaxed">${para}</p>`);
        } else {
          // Continue previous paragraph with line break
          if (formattedLines.length > 0) {
            const lastIndex = formattedLines.length - 1;
            formattedLines[lastIndex] = formattedLines[lastIndex].replace('</p>', `<br>${para}</p>`);
          } else {
            formattedLines.push(`<p class="mb-4 leading-relaxed">${para}</p>`);
          }
        }
      }
    }

    return formattedLines.join('');
  });

  let result = formattedSections.join('');
  
  // Remove "Not:" section at the end (but keep GENEL PUAN section)
  const notPatterns = [
    /\n\s*Not\s*:[\s\S]*$/i,
    /\n\s*NOT\s*:[\s\S]*$/i,
  ];
  
  for (const pattern of notPatterns) {
    // Only remove if it's not followed by GENEL PUAN
    const match = result.match(pattern);
    if (match && !result.substring(match.index! + match[0].length).includes('GENEL PUAN')) {
      result = result.replace(pattern, '');
    }
  }

  // Final defense-in-depth pass before this string lands in
  // dangerouslySetInnerHTML. See sanitizeHtml() at the top of this file.
  return sanitizeHtml(result.trim());
}
